import {
	Address,
	BusinessContact,
	createResource,
	ErrorDocument,
	EvaluationScores,
	FullName,
	getResource,
	OkDocument,
	Permissible,
	Phone,
	Resource,
	updateResource,
	uploadResource,
} from "./common"

import {err, ok, Result} from "neverthrow"
import {
	AnnualIncome,
	ApplicationDocument,
	ApplicationDocumentSide,
	Assignee,
	BusinessAccountPurpose,
	BusinessAnnualRevenue,
	BusinessIndustry,
	BusinessNumberOfEmployees,
	BusinessSourceOfFunds,
	BusinessTransactionVolume,
	BusinessVertical,
	CashFlow,
	DecisionByEnum,
	DocumentLink,
	EntityDeclineReason,
	EvaluationOutcome,
	IndividualAccountPurpose,
	IndividualSourceOfFunds,
	IndividualTransactionVolume,
	IndustryType,
	Occupation,
	Officer,
	DocumentTarget,
	PatchApplicationDocument,
	PatchProductApplicationDocument,
	Profession,
	SoleProprietorshipAnnualRevenue,
	SoleProprietorshipNumberOfEmployees,
	SolePropTransactionVolume,
	SourceOfIncome,
} from "./application"
import {ReevaluatedIndividualProductApplicationData} from "../components/ReevaluateProductApplication/Forms/ReevaluateIndividualProductApplicationForm/ReevaluateIndividualProductApplicationForm"
import {ReevaluatedBusinessProductApplicationData} from "../components/ReevaluateProductApplication/Forms/ReevaluateBusinessProductApplicationForm/ReevaluateBusinessProductApplicationForm"
import {ReevaluatedBeneficialOwnerApplicationData} from "../components/ReevaluateApplication/Forms/ReevaluateBeneficialOwnerForm/ReevaluateBeneficialOwnerForm"
import {ReevaluatedOfficerApplicationData} from "../components/ReevaluateApplication/Forms/ReevaluateOfficerForm/ReevaluateOfficerForm"

type IndividualProductApplicationSensitiveFields =
	| "fullName"
	| "ssn"
	| "passport"
	| "matriculaConsular"
	| "nationality"
	| "dateOfBirth"
	| "ein"

export type PatchIndividualProductApplicationData = Omit<
	ReevaluatedIndividualProductApplicationData,
	IndividualProductApplicationSensitiveFields
>

type BusinessProductApplicationSensitiveFields = "name" | "ein"

export type PatchBusinessProductApplicationData = Omit<
	ReevaluatedBusinessProductApplicationData,
	BusinessProductApplicationSensitiveFields
>

type BeneficialOwnerProductApplicationSensitiveFields =
	| "fullName"
	| "ssn"
	| "passport"
	| "matriculaConsular"
	| "nationality"
	| "dateOfBirth"

export type PatchBeneficialOwnerProductApplicationData = Omit<
	ReevaluatedBeneficialOwnerApplicationData,
	BeneficialOwnerProductApplicationSensitiveFields
>

export function isBusinessProductApplicationDocument(
	application: OkDocument<ProductApplication>
): application is OkDocument<BusinessProductApplication> {
	return application.data.type === "businessProductApplication"
}

export function isIndividualProductApplicationDocument(
	application: OkDocument<ProductApplication>
): application is OkDocument<IndividualProductApplication> {
	return application.data.type === "individualProductApplication"
}

export function getProductApplicationName(application: ProductApplication) {
	switch (application.type) {
		case "businessProductApplication":
			return application.attributes.name
		case "individualProductApplication":
			return `${application.attributes.fullName.first} ${application.attributes.fullName.last}`
	}
}

export async function findProductApplication(
	accessToken: string,
	offset: number,
	limit: number,
	status: ProductApplicationStatus[],
	sort: string,
	searchQuery: string,
	orgs?: string[],
	requestedProduct?: ProductType[],
	decisionMethod?: DecisionMethodType[],
	assignees?: Assignee[],
	customerId?: string
): Promise<Result<OkDocument<Array<ProductApplication>>, ErrorDocument>> {
	if (status?.length === 0) return ok({data: []})
	if (decisionMethod?.length === 0) return ok({data: []})
	if (assignees?.length === 0) return ok({data: []})
	if (requestedProduct?.length === 0) return ok({data: []})

	const searchQueryValue = searchQuery !== "" ? searchQuery : null
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const productFilter = requestedProduct ?? null
	const assigneeFilter =
		assignees && !Object.values(Assignee).every((assignee) => assignees.includes(assignee)) ? assignees : null
	const decisionMethodFilter = decisionMethod ?? null

	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			status: status,
			query: searchQueryValue,
			orgs: orgFilter,
			requestedProduct: productFilter,
			decisionMethod: decisionMethodFilter,
			assignee: assigneeFilter,
			customerId: customerId || null,
		},
		include: "org",
		sort,
	}

	return await getResource<Array<ProductApplication>>("product-applications", accessToken, query)
}

export async function getProductApplication(accessToken: string, id: string) {
	return await getResource<ProductApplication>(`product-applications/${id}`, accessToken, {
		include: "org,documents,beneficialOwners,officer",
	})
}

export async function approveProductApplication(
	accessToken: string,
	application: ProductApplication,
	reason: string,
	evaluatedAt: Date
) {
	const data = {
		type: application.type,
		attributes: {
			reason,
			evaluatedAt,
		},
	}

	return await createResource<ProductApplication>(`product-applications/${application.id}/approve`, accessToken, data)
}

export async function denyProductApplication(
	accessToken: string,
	application: ProductApplication,
	reason: string,
	reasons: EntityDeclineReason[],
	evaluatedAt: Date
) {
	const data = {
		type: application.type,
		attributes: {
			reason,
			reasons,
			evaluatedAt,
		},
	}

	return await createResource<ProductApplication>(`product-applications/${application.id}/deny`, accessToken, data)
}

export async function cancelProductApplication(accessToken: string, application: ProductApplication, reason: string) {
	const data = {
		type: application.type,
		attributes: {
			reason,
		},
	}

	return await createResource<ProductApplication>(`product-applications/${application.id}/cancel`, accessToken, data)
}

export async function requestProductApplicationDocument(
	accessToken: string,
	productApplicationId: string,
	documentType: string,
	target?: DocumentTarget
) {
	if (!target) {
		return err({errors: [{title: "An unexpected error has occurred", status: "400"}]})
	}

	const data = {
		type: documentType,
		relationships: buildProductApplicationDocumentRelationships(target),
	}

	return await createResource<ApplicationDocument>(
		`product-applications/${productApplicationId}/documents/request`,
		accessToken,
		data
	)
}

function buildProductApplicationDocumentRelationships(target: DocumentTarget) {
	if ("officerId" in target) {
		return {officer: {data: {type: "officer", id: target.officerId}}}
	}
	if ("beneficialOwnerId" in target) {
		return {beneficialOwner: {data: {type: "beneficialOwner", id: target.beneficialOwnerId}}}
	}
	return {productApplication: {data: {type: "productApplication", id: target.applicationId}}}
}

export enum ProductType {
	Banking = "Banking",
	BillPay = "BillPay",
	Capital = "Capital",
}

export enum DecisionMethodTypeEnum {
	Manually = "Manually",
	InProgress = "InProgress",
	Automatically = "Automatically",
}

export type DecisionMethodType = keyof typeof DecisionMethodTypeEnum

export enum ProductApplicationStatus {
	AwaitingDocuments = "AwaitingDocuments",
	PendingReview = "PendingReview",
	Approved = "Approved",
	Denied = "Denied",
	Pending = "Pending",
	Canceled = "Canceled",
	PendingSubmission = "PendingSubmission",
	PreApproved = "PreApproved",
}

export const reevaluableProductApplicationStatuses: ProductApplicationStatus[] = [
	ProductApplicationStatus.PendingReview,
	ProductApplicationStatus.AwaitingDocuments,
]

export type ProductApplicationOfficerResource = {
	type: "officer"
	id: string
	attributes: Officer
	relationships: {
		application: {
			data: {
				type: "productApplication"
				id: string
			}
		}
	}
}

export function isOfficer(resource: Resource): resource is ProductApplicationOfficerResource {
	return resource.type === "officer"
}

export type BusinessProductApplication = {
	type: "businessProductApplication"
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		status: ProductApplicationStatus
		requestedProduct: ProductType
		message: string
		preApprovedEvaluatedAt?: Date
		decisionMethod?: DecisionMethodType
		decisionUserId?: string
		decisionBy?: DecisionByEnum
		decisionReason?: string
		decisionDateTime?: Date
		name: string
		dba?: string
		ein: Permissible<string>
		address: Permissible<Address>
		phone: Permissible<Phone>
		stateOfIncorporation: string
		entityType?: "Corporation" | "LLC"
		contact: BusinessContact
		website?: string
		industry?: IndustryType
		annualRevenue?: BusinessAnnualRevenue
		numberOfEmployees?: BusinessNumberOfEmployees
		cashFlow?: CashFlow
		yearOfIncorporation?: string
		countriesOfOperation?: string[]
		stockSymbol?: string
		businessVertical?: BusinessVertical
		assignee?: Assignee
		operatingAddress?: Permissible<Address>
		evaluationId?: string
		evaluationOutcome?: EvaluationOutcome
		evaluationFlags?: string[]
		evaluationEntityId?: string
		evaluationCodes?: string[]
		tags: {[key: string]: string}
		attestedNoBeneficialOwners?: boolean
		businessIndustry?: BusinessIndustry
		sourceOfFunds?: BusinessSourceOfFunds
		sourceOfFundsDescription?: string
		accountPurpose?: BusinessAccountPurpose
		accountPurposeDetail?: string
		transactionVolume?: BusinessTransactionVolume
		transactionVolumeDescription?: string
		isRegulated?: boolean
		regulatorName?: string
		usNexus?: string[]
		businessDescription?: string
	}
	relationships: {
		org: {
			data: {type: "org"; id: string}
		}
		customer: {
			data: {type: "businessCustomer"; id: string}
		}
		documents: {
			data: Array<{type: "document"; id: string}>
		}
		officer?: {
			data: {type: "officer"; id: string}
		}
		beneficialOwners?: {
			data: Array<{type: "beneficialOwner"; id: string}>
		}
		preApprovedOnboardingApplication?: {
			data: {type: "businessApplication"; id: string}
		}
		preApprovedProductApplication?: {
			data: {type: "businessProductApplication"; id: string}
		}
	}
}

export type IndividualProductApplication = {
	type: "individualProductApplication"
	id: string
	attributes: {
		createdAt: Date
		updatedAt: Date
		status: ProductApplicationStatus
		requestedProduct: ProductType
		message: string
		preApprovedEvaluatedAt?: Date
		decisionMethod?: DecisionMethodType
		decisionUserId?: string
		decisionBy?: DecisionByEnum
		decisionReason?: string
		decisionDateTime?: Date
		fullName: FullName
		ssn: Permissible<string>
		passport: Permissible<string>
		nationality: Permissible<string>
		matriculaConsular: Permissible<string>
		address: Permissible<Address>
		dateOfBirth: Permissible<Date>
		email: Permissible<string>
		phone: Phone
		ein: Permissible<string>
		dba?: string
		soleProprietorship: boolean
		industry?: IndustryType
		occupation?: Occupation
		annualIncome?: AnnualIncome
		sourceOfIncome?: SourceOfIncome
		annualRevenue?: SoleProprietorshipAnnualRevenue
		numberOfEmployees?: SoleProprietorshipNumberOfEmployees
		businessVertical?: BusinessVertical
		website?: string
		assignee?: Assignee
		evaluationId?: string
		evaluationOutcome?: EvaluationOutcome
		evaluationFlags?: string[]
		evaluationScores?: EvaluationScores
		evaluationEntityId?: string
		evaluationCodes?: string[]
		idTheftScore?: number
		tags: {[key: string]: string}
		sourceOfFunds?: IndividualSourceOfFunds | BusinessSourceOfFunds
		sourceOfFundsDescription?: string
		accountPurpose?: IndividualAccountPurpose | BusinessAccountPurpose
		accountPurposeDetail?: string
		transactionVolume?: IndividualTransactionVolume | SolePropTransactionVolume
		transactionVolumeDescription?: string
		profession?: Profession
		businessIndustry?: BusinessIndustry
		usNexus?: string[]
		isIncorporated?: boolean
		stateOfIncorporation?: string
		yearOfIncorporation?: string
		countriesOfOperation?: string[]
		businessDescription?: string
	}
	relationships: {
		org: {
			data: {type: "org"; id: string}
		}
		customer: {
			data: {type: "individualCustomer"; id: string}
		}
		documents: {
			data: Array<{type: "document"; id: string}>
		}
		preApprovedOnboardingApplication?: {
			data: {type: "individualApplication"; id: string}
		}
		preApprovedProductApplication?: {
			data: {type: "individualProductApplication"; id: string}
		}
	}
}

export type ProductApplication = BusinessProductApplication | IndividualProductApplication

export enum ProductApplicationsColumns {
	id = "Id",
	name = "Name",
	type = "Type",
	org = "Org",
	status = "Status",
	customerId = "Customer ID",
	createdAt = "Created At",
	updatedAt = "Updated At",
}

export type AllowedProductApplicationsColumns =
	| ProductApplicationsColumns.id
	| ProductApplicationsColumns.name
	| ProductApplicationsColumns.type
	| ProductApplicationsColumns.org
	| ProductApplicationsColumns.status
	| ProductApplicationsColumns.customerId
	| ProductApplicationsColumns.createdAt
	| ProductApplicationsColumns.updatedAt

export function businessProductApplicationPreApprovedLink(productApplication: BusinessProductApplication) {
	const onboardingRelationship = productApplication.relationships.preApprovedOnboardingApplication
	const productRelationship = productApplication.relationships.preApprovedProductApplication

	return productApplicationPreApprovedLink(onboardingRelationship?.data.id, productRelationship?.data.id)
}

export function individualProductApplicationPreApprovedLink(productApplication: IndividualProductApplication) {
	const onboardingRelationship = productApplication.relationships.preApprovedOnboardingApplication
	const productRelationship = productApplication.relationships.preApprovedProductApplication

	return productApplicationPreApprovedLink(onboardingRelationship?.data.id, productRelationship?.data.id)
}

function productApplicationPreApprovedLink(onboardingRelationshipId?: string, productRelationshipId?: string) {
	if (onboardingRelationshipId) {
		return {
			link: `/applications/${onboardingRelationshipId}`,
			text: `Application #${onboardingRelationshipId}`,
		}
	} else if (productRelationshipId) {
		return {
			link: `/product-applications/${productRelationshipId}`,
			text: `Application #${productRelationshipId}`,
		}
	} else {
		return undefined
	}
}

export async function patchIndividualProductApplication(
	accessToken: string,
	applicationId: string,
	data: PatchIndividualProductApplicationData,
	reasonText: string
) {
	const body = {
		type: "individualProductApplication",
		attributes: {
			address: data.address,
			phone: data.phone,
			email: data.email,
			dba: data.dba,
			website: data.website,
			industry: data.industry,
			occupation: data.occupation,
			annualIncome: data.annualIncome,
			sourceOfIncome: data.sourceOfIncome,
			annualRevenue: data.annualRevenue,
			numberOfEmployees: data.numberOfEmployees,
			businessVertical: data.businessVertical,
			reasonText,
		},
	}
	return await updateResource<IndividualProductApplication>(`product-applications/${applicationId}`, accessToken, body)
}

export async function patchBusinessProductApplication(
	accessToken: string,
	applicationId: string,
	data: PatchBusinessProductApplicationData,
	reasonText: string
) {
	const body = {
		type: "businessProductApplication",
		attributes: {
			dba: data.dba,
			address: data.address,
			operatingAddress: data.operatingAddress,
			phone: data.phone,
			stateOfIncorporation: data.stateOfIncorporation,
			businessVertical: data.businessVertical,
			yearOfIncorporation: data.yearOfIncorporation,
			website: data.website,
			numberOfEmployees: data.numberOfEmployees,
			annualRevenue: data.annualRevenue,
			cashFlow: data.cashFlow,
			industry: data.industry,
			countriesOfOperation: data.countriesOfOperation,
			contact: data.contact,
			reasonText,
		},
	}
	return await updateResource<BusinessProductApplication>(`product-applications/${applicationId}`, accessToken, body)
}

export async function patchProductApplicationOfficer(
	accessToken: string,
	productApplicationId: string,
	officerId: string,
	data: ReevaluatedOfficerApplicationData,
	reasonText: string
) {
	const body = {
		type: "officer",
		attributes: {
			fullName: data.fullName,
			address: data.address,
			dateOfBirth: data.dateOfBirth,
			email: data.email,
			phone: data.phone,
			ssn: data.ssn,
			passport: data.passport,
			nationality: data.nationality,
			matriculaConsular: data.matriculaConsular,
			occupation: data.occupation,
			annualIncome: data.annualIncome,
			sourceOfIncome: data.sourceOfIncome,
			reasonText,
		},
		relationships: {
			productApplication: {
				data: {
					type: "businessProductApplication",
					id: productApplicationId,
				},
			},
		},
	}
	return await updateResource<ProductApplicationOfficerResource>(
		`product-applications/${productApplicationId}/officers/${officerId}`,
		accessToken,
		body
	)
}

export async function patchProductApplicationBeneficialOwner(
	accessToken: string,
	productApplicationId: string,
	beneficialOwnerId: string,
	data: PatchBeneficialOwnerProductApplicationData,
	reasonText: string
) {
	const body = {
		type: "beneficialOwner",
		attributes: {
			address: data.address,
			email: data.email,
			phone: data.phone,
			percentage: data.percentage,
			occupation: data.occupation,
			annualIncome: data.annualIncome,
			sourceOfIncome: data.sourceOfIncome,
			reasonText,
		},
		relationships: {
			productApplication: {
				data: {
					type: "businessProductApplication",
					id: productApplicationId,
				},
			},
		},
	}
	return await updateResource<Resource>(
		`product-applications/${productApplicationId}/beneficial-owners/${beneficialOwnerId}`,
		accessToken,
		body
	)
}

export async function getDocumentLink(
	applicationId: string,
	documentId: string,
	accessToken: string,
	side: ApplicationDocumentSide
) {
	return await getResource<DocumentLink>(
		`product-applications/${applicationId}/documents/${documentId}/link/${side.toLocaleLowerCase()}`,
		accessToken
	)
}

export async function uploadDocument(
	accessToken: string,
	applicationId: string,
	documentId: string,
	documentFile: any,
	documentType: string | undefined,
	documentSide: ApplicationDocumentSide
) {
	return await uploadResource<ProductApplication>({
		path: `product-applications/${applicationId}/documents/${documentId}${
			documentSide === ApplicationDocumentSide.Back ? "/back" : ""
		}`,
		accessToken,
		data: documentFile,
		type: documentType,
	})
}

export async function approveDocument(accessToken: string, applicationId: string, documentId: string) {
	return await createResource<ProductApplication>(
		`product-applications/${applicationId}/documents/${documentId}/approve`,
		accessToken,
		null
	)
}

export async function rejectDocument(
	accessToken: string,
	applicationId: string,
	documentId: string,
	reasonCode: string,
	reason: string
) {
	const data = {
		type: "documentReject",
		attributes: {
			reasonCode,
			reason,
		},
	}

	return await createResource<ProductApplication>(
		`product-applications/${applicationId}/documents/${documentId}/reject`,
		accessToken,
		data
	)
}

export async function updateDocument(
	accessToken: string,
	applicationId: string,
	documentId: string,
	document: PatchApplicationDocument
) {
	console.log("document", document)
	const payload: PatchProductApplicationDocument = {
		...document,
		relationships: {
			productApplication: {
				data: {
					type: "productApplication",
					id: applicationId,
				},
			},
		},
	}

	return await updateResource<ApplicationDocument>(
		`product-applications/${applicationId}/documents/${documentId}`,
		accessToken,
		payload
	)
}
